import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, TemplateRef, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatOption } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslatePipe } from '@ngx-translate/core';
import { firstValueFrom, Observable, of, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AutoUnsubscribe } from '../../auto-unsubscribe.decorator';
import { FORM_AUTO_COMPLETE_DEBOUNCE_TIME } from '../../global';
import { LoaderComponent } from '../../loader/loader.component';
import { FormField } from '../form-field.component';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';

@AutoUnsubscribe()
@Component({
  selector: 'app-form-field-auto',
  templateUrl: './form-field-auto.component.html',
  styleUrls: ['./form-field-auto.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatAutocompleteModule, TranslatePipe, LoaderComponent, MatIcon, MatIconButton],
})
export class FormFieldAutoComponent extends FormField implements OnInit, OnDestroy {
  @Input()
  displayFn: (element: unknown) => string;

  @Input()
  listOptions: (args: string) => unknown[] | Promise<unknown[]> | Observable<unknown[]>;
  options: Observable<unknown[]>;

  @Input()
  displayTemplateRef: TemplateRef<unknown>;

  @Input()
  lazyLoad = false;

  @Output()
  optionSelected: EventEmitter<MatAutocompleteSelectedEvent> = new EventEmitter<MatAutocompleteSelectedEvent>();

  @Input()
  suffixButtonIcon: string;

  @Input()
  suffixButtonAction: () => void;

  @ViewChildren('optionElement') optionElements: QueryList<MatOption>;

  private subscription: Subscription;
  searching = false;
  private commitTimeout: number;

  ngOnInit(): void {
    if (!this.lazyLoad) {
      this.search();
    }

    this.subscription = this.formGroup.controls[this.field].valueChanges.pipe(debounceTime(FORM_AUTO_COMPLETE_DEBOUNCE_TIME)).subscribe(() => {
      this.search();
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  notifyListeners(event: MatAutocompleteSelectedEvent): void {
    this.optionSelected.next(event);
  }

  selectOption(viewValue: string): void {
    setTimeout(() => {
      this.optionElements.find((option: MatOption) => option.selected)?.deselect();
      this.optionElements.find((option: MatOption) => option.viewValue === viewValue)?.select();
    }, 0);
  }

  search(valueModifier?: (value: unknown) => unknown): void {
    let value = this.formGroup.controls[this.field].value;

    if (valueModifier) {
      // The value modifier is a function that can be passed to the search method
      // to modify the value before it is used to search for options.
      value = valueModifier(value);
    }

    if (typeof value === 'string') {
      if (this.commitTimeout) {
        clearTimeout(this.commitTimeout);
      }

      this.searching = true;
      const executeFunction = this.listOptions(value);
      if (executeFunction instanceof Observable) {
        this.options = executeFunction;
        firstValueFrom(this.options).then(() => {
          this.searching = false;
          this.commitTimeout = null;
        });
      } else {
        Promise.resolve(executeFunction).then(
          (res) => {
            this.commitTimeout = window.setTimeout(() => {
              this.options = of(res);
              const viewValue = this.formGroup.controls[this.field].value;
              if (viewValue) this.selectOption(viewValue);
              this.searching = false;
              this.commitTimeout = null;
            }, 0);
          },
          (err) => console.error(err),
        );
      }
    }
  }

  shouldDisplayClearBtn(): boolean {
    return super.shouldDisplayClearBtn();
  }
}
